import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  FileText,
  Clock,
  CheckCircle2,
  LogOut,
  User,
  ChevronRight,
  ChevronLeft,
  Home,
  Calendar,
  MapPin,
  MessageSquare,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  X,
  Settings,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const { t, i18n } = useTranslation();
  
  // Pagination & filtering state
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, total_pages: 0 });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    category_id: "",
    sort_by: "created_at",
    sort_order: "desc"
  });

  useEffect(() => {
    fetchCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?lang=${i18n.language?.startsWith('es') ? 'es' : 'en'}`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      });
      
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.category_id) params.append("category_id", filters.category_id);

      const response = await axios.get(`${API}/projects/customer?${params}`);
      setProjects(response.data.projects);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      category_id: "",
      sort_by: "created_at",
      sort_order: "desc"
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = filters.search || filters.status || filters.category_id;

  const getStatusBadge = (status) => {
    const config = {
      open: { label: "Open", style: "bg-green-100 text-green-700 border-green-200" },
      in_progress: { label: "In Progress", style: "bg-yellow-100 text-yellow-700 border-yellow-200" },
      completed: { label: "Completed", style: "bg-blue-100 text-blue-700 border-blue-200" },
      cancelled: { label: "Cancelled", style: "bg-red-100 text-red-700 border-red-200" },
    };
    return config[status] || config.open;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar - Desktop */}
      <aside className="dashboard-sidebar hidden md:block">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#0B1F3B]" />
            </div>
            <span className="text-xl font-extrabold text-white" style={{ fontFamily: 'Manrope' }}>
              MyProjectBidder
            </span>
          </div>

          <nav className="space-y-2">
            <Link
              to="/customer"
              className="sidebar-link active flex items-center gap-3 px-4 py-3 rounded-lg text-white"
            >
              <Home className="w-5 h-5" />
              {t("nav.dashboard")}
            </Link>
            <Link
              to="/customer/new-project"
              className="sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white"
            >
              <Plus className="w-5 h-5" />
              {t("nav.newProject")}
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                </div>
              )}
              <div className="overflow-hidden">
                <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                <p className="text-slate-400 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <NotificationBell />
          </div>
          <Link
            to="/settings"
            className="sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white"
            data-testid="settings-link"
          >
            <Settings className="w-5 h-5" />
            {t("common.settings")}
          </Link>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("common.signOut")}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-[#0B1F3B] text-white p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#0B1F3B]" />
            </div>
            <span className="font-bold">MyProjectBidder</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/customer/new-project")}
              className="text-white hover:bg-white/10"
            >
              <Plus className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-content p-4 md:p-8" data-testid="customer-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
              {t("customer.welcomeBack", { name: user?.name?.split(" ")[0] })}
            </h1>
            <p className="text-slate-600 mt-1 text-sm md:text-base">
              {t("customer.manageProjects")}
            </p>
          </div>
          <Button
            onClick={() => navigate("/customer/new-project")}
            className="gold-gradient text-[#0B1F3B] hover:brightness-110 font-bold px-4 md:px-6 w-full md:w-auto"
            data-testid="new-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t("customer.newProject")}
          </Button>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="pl-9 h-10"
                data-testid="search-input"
              />
            </div>

            {/* Filter Toggle (Mobile) */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge className="ml-2 bg-[#D4AF37] text-[#0B1F3B]">!</Badge>
              )}
            </Button>

            {/* Filters - Desktop always visible, Mobile toggle */}
            <div className={`flex flex-col md:flex-row gap-3 ${showFilters ? 'block' : 'hidden md:flex'}`}>
              <Select value={filters.status} onValueChange={(v) => handleFilterChange("status", v)}>
                <SelectTrigger className="w-full md:w-[140px] h-10" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.category_id} onValueChange={(v) => handleFilterChange("category_id", v)}>
                <SelectTrigger className="w-full md:w-[160px] h-10" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={`${filters.sort_by}-${filters.sort_order}`} onValueChange={(v) => {
                const [sortBy, sortOrder] = v.split("-");
                setFilters(prev => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
              }}>
                <SelectTrigger className="w-full md:w-[160px] h-10" data-testid="sort-select">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">Newest First</SelectItem>
                  <SelectItem value="created_at-asc">Oldest First</SelectItem>
                  <SelectItem value="title-asc">Title A-Z</SelectItem>
                  <SelectItem value="title-desc">Title Z-A</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} className="h-10">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Projects List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#0B1F3B]">{t("customer.yourProjects")}</h2>
            <span className="text-sm text-slate-500">{pagination.total} total</span>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="spinner mx-auto"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="p-8 md:p-16 text-center">
              <FileText className="w-12 md:w-16 h-12 md:h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-[#0B1F3B] mb-2">
                {hasActiveFilters ? t("customer.noMatchingProjects") : t("customer.noProjectsYet")}
              </h3>
              <p className="text-slate-500 mb-6">
                {hasActiveFilters 
                  ? t("customer.adjustFilters") 
                  : t("customer.createFirstProject")}
              </p>
              {!hasActiveFilters && (
                <Button
                  onClick={() => navigate("/customer/new-project")}
                  className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("customer.createProject")}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100">
                {projects.map((project) => {
                  const statusInfo = getStatusBadge(project.status);
                  return (
                    <Link
                      key={project.id}
                      to={`/customer/project/${project.id}`}
                      className="block p-4 md:p-6 hover:bg-slate-50 transition-colors duration-200"
                      data-testid={`project-${project.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-semibold text-[#0B1F3B] truncate">{project.title}</h3>
                            <Badge className={`${statusInfo.style} border text-xs`}>
                              {statusInfo.label}
                            </Badge>
                            {project.estimate_count > 0 && (
                              <Badge className="bg-[#D4AF37]/20 text-[#0B1F3B] border border-[#D4AF37] text-xs">
                                <MessageSquare className="w-3 h-3 mr-1" />
                                {project.estimate_count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-2">{project.description}</p>
                          <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3 md:w-4 md:h-4" />
                              {project.category_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                              {project.city}
                            </span>
                            <span className="hidden sm:flex items-center gap-1">
                              <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                              {formatDate(project.created_at)}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="p-4 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Page {pagination.page} of {pagination.total_pages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.total_pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
